import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { getMyProfile, type ProfileDto } from '../../api/users';
import { listUserAssignments, type AssignmentDto } from '../../api/hr-assignment';
import { getRecords, type RecordDetailDto } from '../../api/hr-records';
import { fetchUserAwards, type UserAwardsDto } from '../../api/hr-awards';
import { fetchUserEvaluations, type UserEvaluationDto } from '../../api/hr-evaluations';
import { fetchUserTrainings, type UserTrainingDto } from '../../api/hr-trainings';
import {
  listMyProfileRequests, createProfileRequest, cancelProfileRequest,
  REQUEST_FIELD_LABEL, type ProfileRequestDto, type RequestField,
} from '../../api/hr-profile-requests';
import { fetchMyAccessLog, type AccessLogRow } from '../../api/hr-people';
import { JobGradeSelect } from '../../components/JobGradeSelect';
import './MyRecordCardPage.css';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1). ③ 표를 정본 Table 로(E1).
import { Button, Table, type TableColumn } from '../../components/shared';
// ★ 인사기록 표의 «칸과 말»은 한 벌뿐이다 — 담당자 화면(`StaffRecordSection`)과 나눠 쓴다.
//   여기서 다시 그리면 두 화면이 또 갈라진다(2026-09-02 이전에 실제로 갈라져 있었다:
//   담당자는 말소·감경을 못 봤고, 인사평가 칸 차례가 반대였다).
import {
  ASSIGNMENT_COLUMNS, EDUCATION_COLUMNS, CAREER_COLUMNS, AWARD_COLUMNS,
  EVALUATION_COLUMNS, COURSE_COLUMNS, credentialColumns,
  courseRowClass, credentialRowClass,
} from './hrRecordTables';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/** 본인 카드는 자격·어학을 **탭으로 갈라** 보므로 종류 칸을 세우지 않는다(줄마다 같은 값이다). */
const MY_CREDENTIAL_COLUMNS = credentialColumns(false);

/**
 * 내 인사기록카드 — 직원 화면 (시안 B2).
 *
 * ★★ **새 표도 새 규칙도 없다.** A19~A22 를 만들 때마다 읽기 라우트를 «본인도» 로 열어 둔
 *    자리가 여기다 — 이 화면은 그 여섯을 탭으로 세우는 것이 전부다(A18 인사 통계와 같은 모양).
 * ★★ **읽기 전용이다.** 시안이 「고칠 수 있는 것은 본인이 관리하는 칸뿐」 이라고 못박았고,
 *    그 칸을 고치는 화면은 **이미 있다**(내 프로필). 여기에 편집 UI 를 또 만들면 같은 값을
 *    두 곳에서 고치게 되고, 한쪽만 고쳐지는 날이 온다 — 그래서 **링크로 보낸다.**
 * ★ **탭을 열 때 부른다.** 여덟을 한꺼번에 부르면 첫 화면이 그만큼 늦다.
 * ★ 시안에 있으나 **지금 담을 칸이 없는 것**(주소 · 비상연락처 · 은행계좌)과 **아직 없는
 *   기능**(정정 요청 · 내 문서)은 화면이 그렇게 말한다 — 빈 칸으로 두면 «비어 있다» 로 읽힌다.
 */

type Tab = 'PROFILE' | 'REQUEST' | 'ASSIGNMENT' | 'RECORD' | 'AWARD' | 'EVALUATION' | 'COURSE' | 'LICENSE' | 'LANGUAGE' | 'ACCESS';

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'PROFILE', label: '신상' },
  { key: 'REQUEST', label: '정정 요청' },
  { key: 'ASSIGNMENT', label: '인사발령' },
  { key: 'RECORD', label: '경력' },
  { key: 'AWARD', label: '포상/징계' },
  { key: 'EVALUATION', label: '인사평가' },
  { key: 'COURSE', label: '교육' },
  { key: 'LICENSE', label: '자격' },
  { key: 'LANGUAGE', label: '어학' },
  /**
   * ★★ **내 정보를 본 기록** (자리 재정의 판 3, 2026-08-28).
   *    「남의 개인정보를 열면 기록이 남고, **본인이 그 기록을 본다**」 — 그 «본다» 가 여기다.
   *    담당자용 이력 화면보다 **이쪽을 먼저** 세운다: 기록이 남는다는 사실을 정작 당사자가
   *    모르면 남기는 뜻의 절반이 없다.
   */
  { key: 'ACCESS', label: '열람 기록' },
];

/**
 * 이 칸을 누가 움직이나 — 시안의 회색 배지.
 *
 * ★ `BOTH`(담당자 · 발령)를 2026-08-27 에 더했다. 직위는 **본인이 못 바꾸지만**(서버가 403)
 *   담당자가 명부에서도, 발령으로도 바꾼다 — «발령으로만» 은 그때도 지금도 정확하지 않다.
 *   배지는 «내가 못 바꾼다» 를 말하는 것이므로, 어떻게 바뀌는지를 있는 그대로 적는다.
 */
/**
 * 어떤 자격으로 열었나 — 서버가 남긴 `scope`·`via` 를 사람 말로.
 * ★ 옛 줄에는 이 값이 없다(그때는 안 남겼다) → 「—」.
 */
const VIA_LABEL: Record<string, string> = {
  ALL: '인사 담당', TEAM: '팀장', ADMIN: '회사 관리자',
  TEAM_LEADER: '팀장', GRANT: '담당자 부여', ROLE: '권한',
};

/** 「2026-08-28 14:03」 — 날짜만으로는 「언제 열었나」에 답이 안 된다. */
function accessTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * 내 정보를 본 기록 — **이 화면에만 있는 표**라 공용으로 뽑지 않는다
 * (담당자 화면에는 같은 것이 없다. 없는 것을 미리 공용으로 두면 이름만 늘어난다).
 */
const ACCESS_COLUMNS: ReadonlyArray<TableColumn<AccessLogRow>> = [
  { key: 'ts', header: '언제', width: '150px', render: (r) => <span className="hrpage__dim hrcard__nowrap">{accessTime(r.ts)}</span> },
  {
    key: 'actor', header: '누가', width: '180px',
    render: (r) => (
      <>
        {r.actor ? r.actor.name : '(지워진 계정)'}
        {r.actor?.position && <span className="hrpage__dim">{` · ${r.actor.position}`}</span>}
      </>
    ),
  },
  { key: 'label', header: '무엇을' },
  { key: 'via', header: '자격', width: '120px', render: (r) => <span className="hrpage__dim">{VIA_LABEL[r.via ?? ''] ?? '—'}</span> },
];

type Owner = 'ADMIN' | 'ASSIGNMENT' | 'BOTH' | 'MINE';
const OWNER_LABEL: Record<Owner, string> = {
  ADMIN: '담당자만', ASSIGNMENT: '발령으로만', BOTH: '담당자 · 발령', MINE: '내가 관리',
};

export function MyRecordCardPage() {
  const me = useAuthStore((s) => s.user);
  const myId = me ? String(me.id) : null;

  const [tab, setTab] = useState<Tab>('PROFILE');
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');

  const [assignments, setAssignments] = useState<AssignmentDto[] | null>(null);
  const [records, setRecords] = useState<RecordDetailDto | null>(null);
  const [awards, setAwards] = useState<UserAwardsDto | null>(null);
  const [evaluations, setEvaluations] = useState<UserEvaluationDto[] | null>(null);
  const [trainings, setTrainings] = useState<UserTrainingDto | null>(null);
  /** 그 탭을 못 읽었다 — 서버가 아직 그 라우트를 모를 수 있다(탭 하나만 접는다). */
  const [folded, setFolded] = useState<Partial<Record<Tab, boolean>>>({});

  const [access, setAccess] = useState<AccessLogRow[] | null>(null);
  const [requests, setRequests] = useState<ProfileRequestDto[] | null>(null);
  /** 요청 폼 — 어느 칸을 무엇으로 바꿔 달라는가. */
  const [reqField, setReqField] = useState<RequestField>('position');
  const [reqValue, setReqValue] = useState('');
  const [reqReason, setReqReason] = useState('');
  const [reqError, setReqError] = useState<string | null>(null);
  const [reqBusy, setReqBusy] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await getMyProfile();
        if (alive) { setProfile(p); setState('ok'); }
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, []);

  /** 탭을 열 때 그 탭의 것만 부른다 — 한 번 읽으면 다시 묻지 않는다. */
  const loadTab = useCallback(async (next: Tab) => {
    if (!myId) return;
    const fold = () => setFolded((f) => ({ ...f, [next]: true }));
    try {
      if (next === 'REQUEST' && requests === null) {
        setRequests((await listMyProfileRequests()).rows);
      } else if (next === 'ASSIGNMENT' && assignments === null) {
        setAssignments((await listUserAssignments(myId)).assignments);
      } else if (next === 'RECORD' && records === null) {
        setRecords(await getRecords(myId));
      } else if (next === 'AWARD' && awards === null) {
        setAwards(await fetchUserAwards(myId));
      } else if (next === 'EVALUATION' && evaluations === null) {
        setEvaluations((await fetchUserEvaluations(myId)).rows);
      } else if ((next === 'COURSE' || next === 'LICENSE' || next === 'LANGUAGE') && trainings === null) {
        setTrainings(await fetchUserTrainings(myId));
      } else if (next === 'ACCESS' && access === null) {
        // 옛 서버에는 이 라우트가 없다(404) — 그때는 이 탭만 접힌다.
        setAccess((await fetchMyAccessLog({ limit: 50 })).rows);
      }
    } catch {
      fold();
    }
  }, [myId, assignments, records, awards, evaluations, trainings, requests, access]);

  useEffect(() => { void loadTab(tab); }, [tab, loadTab]);

  const org = profile?.org ?? null;
  const rows = useMemo<Array<{ label: string; value: string; owner: Owner }>>(() => [
    { label: '이름', value: profile?.name ?? '—', owner: 'ADMIN' },
    { label: '사번', value: profile?.employeeNumber ?? '—', owner: 'ADMIN' },
    {
      label: '부서 · 팀',
      value: org ? [org.divisionName, org.teamName].filter(Boolean).join(' · ') : '회사 직속',
      owner: 'ASSIGNMENT',
    },
    // ★ 직위는 본인이 못 바꾼다(2026-08-27 · 서버 403) — 담당자가 명부에서, 또는 발령으로 바꾼다.
    { label: '직위', value: profile?.position ?? '—', owner: 'BOTH' },
    { label: '입사일', value: profile?.hireDate ?? '—', owner: 'ADMIN' },
    { label: '고용형태', value: profile?.employmentType ?? '—', owner: 'ADMIN' },
    { label: '휴대전화', value: profile?.phone ?? '—', owner: 'MINE' },
    { label: '내선', value: profile?.extension ?? '—', owner: 'MINE' },
    { label: '자리', value: profile?.deskLocation ?? '—', owner: 'MINE' },
  ], [profile, org]);

  const creds = trainings?.credentials ?? [];

  /**
   * 정정 요청 이력.
   * ★ **목록의 기능은 단추다**(정본 P3 행 작업 · 2026-09-02 결정) — 종전의 글자 링크(`hrcard__link`)를
   *   `quiet-danger` 단추로 바꾼다. 되돌리는 일이라 조용하되 위험색을 쓴다.
   * ★ `withdraw`·`reqBusy` 를 봐야 해서 컴포넌트 안에서 만든다(밖으로 뺄 수 없다).
   */
  const REQUEST_COLUMNS: ReadonlyArray<TableColumn<ProfileRequestDto>> = [
    { key: 'createdAt', header: '요청일', width: '106px', render: (r) => <span className="hrcard__nowrap">{r.createdAt.slice(0, 10)}</span> },
    { key: 'fieldLabel', header: '칸', width: '120px' },
    {
      key: 'requestedValue', header: '바꿔 달라는 값',
      render: (r) => (
        <>
          {r.requestedValue}
          {r.decisionNote && <div className="hrpage__dim hrcard__why">{r.decisionNote}</div>}
        </>
      ),
    },
    {
      key: 'statusLabel', header: '상태', width: '104px',
      render: (r) => (
        <span className={`hrpage__pill hrcard__req is-${r.status.toLowerCase()}`}>{r.statusLabel}</span>
      ),
    },
    {
      key: 'act', header: <span className="hrrec__sr">작업</span>, align: 'right', width: '92px',
      render: (r) => (r.open ? (
        <Button
          variant="quiet-danger" size="sm"
          aria-label={`${r.fieldLabel} 정정 요청 회수`}
          disabled={reqBusy === r.id}
          onClick={() => withdraw(r.id)}
        >
          회수
        </Button>
      ) : null),
    },
  ];

  /** 지금 화면이 보고 있는 값 — 요청에 함께 실어 보낸다(나중에 값이 바뀌면 근거가 사라진다). */
  const currentOf = (field: RequestField): string | null => {
    const map: Partial<Record<RequestField, string | null | undefined>> = {
      name: profile?.name,
      employeeNumber: profile?.employeeNumber,
      team: org ? [org.divisionName, org.teamName].filter(Boolean).join(' · ') : null,
      position: profile?.position,
      hireDate: profile?.hireDate,
      employmentType: profile?.employmentType,
    };
    return map[field] ?? null;
  };

  const submitRequest = async () => {
    if (!reqValue.trim()) { setReqError('바꿔 달라는 값을 적어 주세요'); return; }
    setReqBusy('new');
    setReqError(null);
    try {
      await createProfileRequest({
        field: reqField,
        requestedValue: reqValue.trim(),
        currentValue: currentOf(reqField),
        reason: reqReason.trim() || null,
      });
      setReqValue('');
      setReqReason('');
      setRequests((await listMyProfileRequests()).rows);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setReqError(msg ?? '요청을 넣지 못했습니다');
    } finally {
      setReqBusy(null);
    }
  };

  const withdraw = async (id: string) => {
    setReqBusy(id);
    setReqError(null);
    try {
      await cancelProfileRequest(id);
      setRequests((await listMyProfileRequests()).rows);
    } catch {
      setReqError('회수하지 못했습니다');
    } finally {
      setReqBusy(null);
    }
  };

  return (
    <div className="hrpage hrcard">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/me/record')}
        scope={scopeFor('/erp/hr/me/record')}
        title="내 인사기록카드"
        description="회사가 가진 내 기록을 한 장으로 봅니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">내 정보를 불러오지 못했습니다 — 잠시 뒤 다시 시도해 주세요.</div>
      )}

      {state !== 'folded' && (
        <>
          <div className="hrcert__tabs hrcard__tabs" role="group" aria-label="탭">
            {TABS.map((t) => (
              <button
                key={t.key} type="button"
                className={`hrcert__tab${tab === t.key ? ' is-on' : ''}`}
                onClick={() => setTab(t.key)}
                aria-pressed={tab === t.key}
              >
                {t.label}
              </button>
            ))}
          </div>

          {folded[tab] && (
            <div className="hrpage__note">이 탭을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
          )}

          {tab === 'PROFILE' && (
            <>
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  기본 정보
                  <span className="hrpage__panels">회색 칸은 담당자 · 발령으로만 바뀝니다</span>
                </h2>
                <table className="hrpage__tbl">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.label}>
                        <td className="hrcard__k">{r.label}</td>
                        <td className={r.owner === 'MINE' ? '' : 'hrpage__dim'}>{r.value}</td>
                        <td className="hrpage__act">
                          <span className={`hrpage__pill hrcard__own is-${r.owner.toLowerCase()}`}>
                            {OWNER_LABEL[r.owner]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <div className="hrpage__note">
                「내가 관리」 칸은 <Link to="/me">내 프로필</Link>에서 고칩니다 — 같은 값을 두 곳에서
                고치지 않도록 이 화면은 읽기 전용입니다.
              </div>
              <div className="hrpage__note">
                시안의 <b>주소 · 비상 연락처 · 은행 계좌</b>는 아직 담을 칸이 없습니다 —
                「정정 요청」 탭에서 <b>그 밖의 것</b>으로 적어 주세요.
                회색 칸은 그 탭에서 <b>정정 요청</b>으로 바꿉니다 — <b>직위</b>도 여기에 포함됩니다
                (내 프로필에서는 더 이상 고칠 수 없습니다).
              </div>
            </>
          )}

          {tab === 'REQUEST' && (
            <>
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  정정 요청
                  <span className="hrpage__panels">회색 칸은 이 길로만 바뀝니다 — 담당자가 보고 답합니다</span>
                </h2>
                <div className="hrcert__form">
                  <label className="hrcert__field">
                    <span>칸</span>
                    <select
                      value={reqField}
                      onChange={(e) => { setReqField(e.target.value as RequestField); setReqError(null); }}
                    >
                      {(Object.keys(REQUEST_FIELD_LABEL) as RequestField[]).map((f) => (
                        <option key={f} value={f}>{REQUEST_FIELD_LABEL[f]}</option>
                      ))}
                    </select>
                  </label>
                  <div className="hrcert__field hrcard__static">
                    <span>지금</span>
                    <p>{currentOf(reqField) ?? '—'}</p>
                  </div>
                  <label className="hrcert__field">
                    <span>바꿔 달라는 값</span>
                    {/* 직위만 마스터에서 고른다(판 2) — 담당자가 받아 그대로 넣을 값이라
                        여기서 지어낸 이름을 적으면 명부에 목록 밖 직위가 하나 더 는다.
                        ★ 마스터는 담당자 전용(403)이라 본인 화면에서는 자유 입력으로 돌아간다 —
                          그때도 지금까지와 똑같이 적을 수 있다. */}
                    {reqField === 'position' ? (
                      <JobGradeSelect
                        value={reqValue} placeholder="고르세요"
                        onChange={(v) => setReqValue(v)}
                      />
                    ) : (
                      <input
                        value={reqValue} maxLength={200} placeholder="예) 과장"
                        onChange={(e) => setReqValue(e.target.value)}
                      />
                    )}
                  </label>
                  <label className="hrcert__field">
                    <span>사유</span>
                    <input
                      value={reqReason} maxLength={200} placeholder="예) 2024-01-01 승진이 반영되지 않았습니다"
                      onChange={(e) => setReqReason(e.target.value)}
                    />
                  </label>
                  <Button variant="primary" onClick={submitRequest} disabled={reqBusy === 'new'}>
                    {reqBusy === 'new' ? '보내는 중…' : '요청하기'}
                  </Button>
                </div>
                {reqError && <p className="hrcert__err" role="alert">{reqError}</p>}
                <p className="hrpage__empty">
                  담을 칸이 없는 것(주소 · 비상 연락처 등)은 «그 밖의 것» 으로 적어 주세요.
                </p>
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  요청 이력
                  <span className="hrpage__panels">{requests?.length ?? 0}건</span>
                </h2>
                <Table
                  caption="정정 요청 이력" className="hrrec__tbl"
                  columns={REQUEST_COLUMNS} rows={requests ?? []} rowKey={(r) => r.id}
                  loading={!requests} empty="아직 넣은 요청이 없습니다."
                />
              </section>
            </>
          )}

          {tab === 'ASSIGNMENT' && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                내 발령 이력
                <span className="hrpage__panels">시행된 것만 보입니다 — 예정은 아직 일어나지 않은 일입니다</span>
              </h2>
              <Table
                caption="내 발령 이력" className="hrrec__tbl"
                columns={ASSIGNMENT_COLUMNS} rows={assignments ?? []} rowKey={(a) => a.id}
                loading={!assignments} empty="아직 시행된 발령이 없습니다."
              />
            </section>
          )}

          {tab === 'RECORD' && (
            <>
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  학력<span className="hrpage__panels">{records?.educations.length ?? 0}건</span>
                </h2>
                <Table
                  caption="학력" className="hrrec__tbl"
                  columns={EDUCATION_COLUMNS} rows={records?.educations ?? []} rowKey={(e) => e.id}
                  loading={!records} empty="등록된 학력이 없습니다."
                />
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  입사 전 경력<span className="hrpage__panels">{records?.careers.length ?? 0}건</span>
                </h2>
                <Table
                  caption="입사 전 경력" className="hrrec__tbl"
                  columns={CAREER_COLUMNS} rows={records?.careers ?? []} rowKey={(c) => c.id}
                  loading={!records} empty="등록된 경력이 없습니다."
                />
              </section>
            </>
          )}

          {tab === 'AWARD' && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                포상 · 징계
                <span className="hrpage__panels">말소된 기록도 남습니다 — 더는 불이익에 쓰지 않을 뿐입니다</span>
              </h2>
              <Table
                caption="포상 · 징계" className="hrrec__tbl"
                columns={AWARD_COLUMNS} rows={awards?.rows ?? []} rowKey={(r) => r.id}
                loading={!awards} empty="기록이 없습니다."
              />
            </section>
          )}

          {tab === 'EVALUATION' && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                인사평가
                <span className="hrpage__panels">확정 전 회차는 등급 · 점수를 아직 말하지 않습니다</span>
              </h2>
              <Table
                caption="인사평가" className="hrrec__tbl"
                columns={EVALUATION_COLUMNS} rows={evaluations ?? []} rowKey={(e) => e.roundId}
                loading={!evaluations} empty="참여한 회차가 없습니다."
              />
            </section>
          )}

          {tab === 'ACCESS' && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                내 정보를 본 기록
                <span className="hrpage__panels">
                  담당자 · 팀장이 내 인사정보를 연 기록입니다 — 내가 본 것은 남지 않습니다
                </span>
              </h2>
              <Table
                caption="내 정보를 본 기록" className="hrrec__tbl"
                columns={ACCESS_COLUMNS} rows={access ?? []} rowKey={(r) => r.id}
                loading={!access} empty="최근 1년 동안 아무도 열어 보지 않았습니다."
              />
            </section>
          )}

          {tab === 'COURSE' && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                교육
                <span className="hrpage__panels">
                  {trainings ? `${trainings.courses.filter((c) => c.done).length} / ${trainings.courses.length}건 이수` : ''}
                </span>
              </h2>
              <Table
                caption="교육" className="hrrec__tbl"
                columns={COURSE_COLUMNS} rows={trainings?.courses ?? []} rowKey={(c) => c.id}
                rowClassName={courseRowClass}
                loading={!trainings} empty="열린 교육이 없습니다."
              />
            </section>
          )}

          {(tab === 'LICENSE' || tab === 'LANGUAGE') && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                {tab === 'LICENSE' ? '자격' : '어학'}
                <span className="hrpage__panels">만료가 없으면 «—» 입니다</span>
              </h2>
              <Table
                caption={tab === 'LICENSE' ? '자격' : '어학'} className="hrrec__tbl"
                columns={MY_CREDENTIAL_COLUMNS}
                rows={creds.filter((c) => c.kind === tab)} rowKey={(c) => c.id}
                rowClassName={credentialRowClass}
                loading={!trainings} empty="등록된 기록이 없습니다."
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}
